import {useRuntimeConfig} from '#app';
import {
	createSanityCdnImageUrl,
	sanityFormatAndSizeParameters,
	sanityImageObjectFromRef
} from '~/lib/sanityImageHelper';

/**
 * A custom HTML element for rendering Sanity images.
 * It fetches and formats the image URL based on the `data-src` attribute
 * and renders the image with appropriate dimensions and styles.
 * @example
 * // Define the Custom the component
 * customElements.define('sanity-image', SanityImage);
 * @example
 * // After defining the custom component, it can be used in the HTML
 * <sanity-image data-src="image-A-300x450-jpg" alt="Lorem ipsum"></sanity-image>
 */
export class SanityImage extends HTMLElement {
	imgUrl: string | undefined;
	largeImgUrl: string | undefined;

	width: string | undefined;
	height: string | undefined;

	sanityConfig = useRuntimeConfig().public.sanity;

	MAX_WIDTH_HEIGHT = 400;

	constructor() {
		super();
		this.attachShadow({mode: 'open'});
	}

	static readonly observedAttributes = ['data-src', 'alt'] as const;

	connectedCallback(): void {
		this.setImageUrl();
	}

	attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
		if ((name === 'data-src') && oldValue !== newValue) {
			this.setImageUrl();
		}

		if ((name === 'alt') && oldValue !== newValue) {
			this.render();
		}
	}

	setImageUrl(): void {
		const dataSrc = this.getAttribute('data-src');

		if (!dataSrc) {
			console.error('SanityImage: Missing "data-src" attribute.');
			return;
		}

		try {
			const imageObject = sanityImageObjectFromRef(dataSrc);

			this.width = String(imageObject.width);
			this.height = String(imageObject.height);

			const cdnImageUrl = createSanityCdnImageUrl(this.sanityConfig.projectId, this.sanityConfig.dataset, imageObject);
			const parameters = sanityFormatAndSizeParameters({
				width: this.MAX_WIDTH_HEIGHT,
				height: this.MAX_WIDTH_HEIGHT
			});
			this.imgUrl = cdnImageUrl + parameters;

			this.largeImgUrl = cdnImageUrl + sanityFormatAndSizeParameters({width: 1920, height: 1920});

			this.render();
		} catch (error) {
			console.error(`SanityImage: Failed to set image URL for "data-src"=${dataSrc}.`, error);
		}
	}

	/**
	 * Generates and returns the CSS styles for the component.
	 * Ensures responsive behavior and maintains aspect ratio of the original image
	 */
	css() {
		return `
			<style>
				:host {
				    display: block;
					height: auto;
					width: min(${this.width}px, ${this.MAX_WIDTH_HEIGHT}px);
					max-width: 100%;
					white-space: normal !important;
					aspect-ratio: ${this.width} / ${this.height};
				}
				
				a, picture, img {
					aspect-ratio: ${this.width} / ${this.height};
					height: auto;
					width: auto;
					max-width: 100%;
					vertical-align: middle;
					display: inline-block;
				}
			</style>
		`;
	}

	render(): void {
		if (this.shadowRoot) {
			if (!this.imgUrl) {
				this.shadowRoot.innerHTML = ``;
			} else {
				const altText = this.getAttribute('alt') ?? '';
				this.shadowRoot.innerHTML = `
				<a href="${this.largeImgUrl ?? this.imgUrl}" target="_blank" rel="noopener noreferrer">
					<picture>
					    <img 
						    src="${this.imgUrl}"
						    alt="${altText}"
						    loading="lazy"
							${this.width ? `width="${this.width}"` : ''}
							${this.height ? `height="${this.height}"` : ''}
						>
					</picture>			
				</a>
				${this.css()}`;
			}
		}
	}
}
